// src/components/kolTabs/PostComposer.js
//
// Mission 031-01 / 031-03: the creator's post composer — a post IS a
// collection with one photo, published through the existing path
// (contentService.createCollection: collection_create, then photos_sync with
// the photo uploaded to the PRIVATE bucket), then the fire-and-forget
// notify-followers fan-out Studio made after a create. Opened from the profile
// feed ("New post", the phone "+") and from the navbar's "Create Post" row.
//
// 031-03: the Create Collection modal's exact design (its shell, header, body
// and footer classes), portaled to <body>; the same modal serves phones (it
// scrolls inside max-h-[90dvh]). 031-07/08: a narrow 480 px panel, one item
// per row; the photo row spans the body like the fields, and
// every field has the same label row (FieldLabel, 031-09); the photo field is a
// 2:1 box. Fields: the photo (ImageUpload, 4:5), the
// caption (600 chars) and the price in USD, prefilled with the creator's
// default (useCollectionBounds; empty = free, the "Free" chip clears it).
//
// The PUBLIC banner is a tiny ~40 px version of the photo, like the locked
// previews: the paid original never goes to the public bucket at full size
// (031-01). After a post, `lunexx:post-created` tells an open feed to refetch.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { DollarSign, ImagePlus, X } from 'lucide-react';
import { contentService } from '../../services/ContentService';
import { supabase } from '../../lib/supabase';
import { useSiweAuth } from '../../context/AuthContext';
import { dollarsToCents, formatTicketUsd } from '../../lib/roomCommerce';
import { DEFAULT_COLLECTION_USD, centsToDollars, useCollectionBounds } from '../../lib/useCollectionBounds';
import { resizeImage } from '../../lib/resizeImage';
import ImageUpload from './ImageUpload';

export const CAPTION_MAX = 600;

// 031-09: ONE label row for every field (Photo, Caption, Price) — the same
// height, margin and type, so the labels share a left edge and a baseline;
// `right` holds the Price row's Free chip
export const FIELD_LABEL_ROW = 'mb-2 flex h-5 items-center justify-between';
export const FIELD_LABEL = 'text-sm font-medium leading-5 text-zinc-400';
function FieldLabel({ htmlFor, right = null, children }) {
  return (
    <div className={FIELD_LABEL_ROW} data-testid="field-label">
      <label className={FIELD_LABEL} htmlFor={htmlFor}>{children}</label>
      {right}
    </div>
  );
}
export const POST_CREATED_EVENT = 'lunexx:post-created';

export default function PostComposer({ open, onClose, creatorId, onPosted, prepare = resizeImage }) {
  const { ensureAuth } = useSiweAuth();
  const { bounds, creatorDefaultCents } = useCollectionBounds(open);
  const [photo, setPhoto] = useState(null);       // the picked image (a data url) or an https url
  const [thumb, setThumb] = useState(null);       // the tiny public version (from the picked file)
  const [caption, setCaption] = useState('');
  const [price, setPrice] = useState(DEFAULT_COLLECTION_USD);
  const [priceError, setPriceError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const priceTouched = useRef(false);

  // a fresh form each time it opens
  useEffect(() => {
    if (!open) return;
    setPhoto(null); setThumb(null); setCaption(''); setPrice(DEFAULT_COLLECTION_USD);
    setPriceError(''); setSubmitError(''); setSubmitting(false);
    priceTouched.current = false;
  }, [open]);

  // the creator's default price, unless they already typed one (or chose Free)
  useEffect(() => {
    if (open && creatorDefaultCents > 0 && !priceTouched.current) setPrice(centsToDollars(creatorDefaultCents));
  }, [open, creatorDefaultCents]);

  if (!open) return null;

  // the original file → its ~40 px public thumbnail (never the photo itself)
  const onFile = (file) => {
    setThumb(null);
    prepare(file, 40, 50, 0.5).then(setThumb).catch(() => setThumb(null));
  };

  const setPhotoValue = (v) => {
    setPhoto(v);
    if (!v) setThumb(null);
  };

  const notifyFollowers = (collectionId) => {
    // fire-and-forget; server-side idempotent (notification_log), never blocks posting
    (async () => {
      try {
        const token = await ensureAuth();
        await supabase.functions.invoke('notify-followers', {
          body: { kind: 'new_collection', collectionId },
          headers: { 'X-Lunexx-Auth': `Bearer ${token}` },
        });
      } catch (e) {
        console.warn('[PostComposer] notify-followers failed:', e?.message);
      }
    })();
  };

  const handleSubmit = async () => {
    if (!photo || submitting) return;
    const cents = dollarsToCents(price);
    if (Number.isNaN(cents) || (cents && (cents < bounds.min_cents || cents > bounds.max_cents))) {
      setPriceError(`Enter a price between ${formatTicketUsd(bounds.min_cents)} and ${formatTicketUsd(bounds.max_cents)}, or leave it empty for free.`);
      return;
    }
    const text = caption.trim();
    setPriceError('');
    setSubmitError('');
    setSubmitting(true);
    try {
      // an https photo (URL mode) is public already; a picked one gets its tiny thumbnail
      const banner = /^https?:\/\//i.test(photo) ? photo : thumb;
      const created = await contentService.createCollection(creatorId, {
        title: text.slice(0, 60).trim() || 'Post',
        description: text,
        priceCents: cents || null,
        bannerUrl: banner || null,
        photos: [{ url: photo, description: text }],
      });
      if (created?.id) notifyFollowers(created.id);
      try { window.dispatchEvent(new CustomEvent(POST_CREATED_EVENT, { detail: { creatorId, collectionId: created?.id ?? null } })); } catch { /* old browsers */ }
      onPosted?.(created);
      onClose?.();
    } catch (err) {
      console.error('Failed to post:', err);
      setSubmitError(err?.message || 'Couldn’t post. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => { if (!submitting) onClose?.(); }} />

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Create Post"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-[480px] bg-[#141414] border border-white/10 rounded-[28px] shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-5 border-b border-white/[0.04] shrink-0 sm:px-7">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
              <ImagePlus size={20} className="text-zinc-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white tracking-tight">Create Post</h3>
              <p className="text-sm font-medium text-zinc-500">Share one photo fans can unlock</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-zinc-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar sm:px-7" data-testid="post-composer-body">
          {/* Photo — the shared FieldLabel row, then the full-width 2:1 photo field (1 px dashed; the
              prompt, or the whole photo with a thin Replace / Remove bar); upload only. The stored photo
              is unchanged (1440 × 1800 bound, 4:5 in the feed) (031-09). */}
          <div className="w-full" data-testid="post-photo-row">
            <FieldLabel>Photo</FieldLabel>
            <ImageUpload value={photo} onChange={setPhotoValue} onFile={onFile} label="Photo" resize={[1440, 1800]}
              allowUrl={false} hideLabel variant="field" />
          </div>

          {/* Caption */}
          <div>
            <FieldLabel htmlFor="post-caption">Caption</FieldLabel>
            <textarea
              id="post-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_MAX))}
              placeholder="Write a caption…"
              rows={3}
              maxLength={CAPTION_MAX}
              className="w-full px-4 py-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.06] text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20 transition-colors resize-none"
            />
            <p className="mt-1 text-right text-[11px] text-zinc-600">{caption.length}/{CAPTION_MAX}</p>
          </div>

          {/* Price */}
          <div>
            <FieldLabel
              htmlFor="post-price"
              right={(
                <button
                  type="button"
                  onClick={() => { priceTouched.current = true; setPrice(''); setPriceError(''); }}
                  className={`h-5 px-2 rounded-md text-[11px] font-medium leading-5 transition-colors ${price === '' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'}`}
                >
                  Free
                </button>
              )}
            >
              Price (USD)
            </FieldLabel>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="number"
                step="0.01"
                min="0"
                id="post-price"
                aria-label="Price (USD)"
                value={price}
                placeholder="Free"
                onChange={(e) => { priceTouched.current = true; setPrice(e.target.value); setPriceError(''); }}
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.06] text-white focus:outline-none focus:border-white/20 transition-colors"
              />
            </div>
            <p className={`mt-1.5 text-[11px] ${priceError ? 'text-red-400' : 'text-zinc-500'}`} data-testid="post-price-hint">
              {priceError || `${formatTicketUsd(bounds.min_cents)}–${formatTicketUsd(bounds.max_cents)} · fans unlock it from their Lunexx balance`}
            </p>
          </div>

          {submitError && <p role="alert" className="text-[12px] text-red-400">{submitError}</p>}
        </div>

        {/* Footer */}
        <div className="px-5 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] border-t border-white/[0.04] bg-white/[0.01] flex items-center justify-end gap-3 shrink-0 sm:px-7">
          <button onClick={onClose} className="px-6 py-3 rounded-2xl text-sm font-medium text-zinc-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!photo || submitting}
            className="px-6 py-3 rounded-2xl text-sm font-bold bg-white text-black hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {submitting && <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />}
            Post
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
